import React, { useState } from 'react';
import { Card } from '../Shared/Card';
import { CheckCircleIcon, ClockIcon, AlertTriangleIcon, FileTextIcon, CalendarIcon, InfoIcon, FilterIcon, DownloadIcon, CheckIcon, XIcon, ArrowRightIcon } from 'lucide-react';
type ComplianceStatus = 'compliant' | 'pending' | 'non_compliant' | 'waived';
type ComplianceCategory = 'regulatory' | 'investor' | 'internal' | 'rating_agency';
interface ComplianceRequirement {
  id: string;
  title: string;
  description: string;
  category: ComplianceCategory;
  status: ComplianceStatus;
  dueDate: string;
  assignee: string;
  lastUpdated: string;
  pools: string[];
  notes?: string;
}
export function ComplianceTracker() {
  const [activeCategory, setActiveCategory] = useState<ComplianceCategory | 'all'>('all');
  const [activeStatus, setActiveStatus] = useState<ComplianceStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Sample compliance requirements data
  const complianceRequirements: ComplianceRequirement[] = [{
    id: 'COMP-001',
    title: 'DBRS Monthly Performance Report',
    description: 'Submit monthly performance data for all loans in DBRS-rated securitizations.',
    category: 'rating_agency',
    status: 'pending',
    dueDate: '2023-05-15',
    assignee: 'Sarah Wilson',
    lastUpdated: '2023-05-01',
    pools: ['Q3 RTL Pool']
  }, {
    id: 'COMP-002',
    title: 'Quarterly Investor Reporting',
    description: 'Prepare and distribute quarterly performance reports to all investors.',
    category: 'investor',
    status: 'compliant',
    dueDate: '2023-06-30',
    assignee: 'Michael Brown',
    lastUpdated: '2023-04-15',
    pools: ['Bridge Loan Package', 'Q3 RTL Pool'],
    notes: 'Last report submitted on April 15, 2023.'
  }, {
    id: 'COMP-003',
    title: 'Annual Anti-Money Laundering Review',
    description: 'Conduct annual review of AML procedures and document compliance.',
    category: 'regulatory',
    status: 'non_compliant',
    dueDate: '2023-05-10',
    assignee: 'Robert Johnson',
    lastUpdated: '2023-04-28',
    pools: ['All Pools'],
    notes: 'Review is overdue. Need to schedule with compliance team immediately.'
  }, {
    id: 'COMP-004',
    title: 'Loan Documentation Audit',
    description: 'Internal audit of loan documentation completeness for all active loans.',
    category: 'internal',
    status: 'pending',
    dueDate: '2023-05-20',
    assignee: 'Sarah Wilson',
    lastUpdated: '2023-05-02',
    pools: ['Q3 RTL Pool', 'Bridge Loan Package']
  }, {
    id: 'COMP-005',
    title: 'REIT Qualification Testing',
    description: 'Quarterly testing to ensure compliance with REIT qualification requirements.',
    category: 'investor',
    status: 'waived',
    dueDate: '2023-04-30',
    assignee: 'Michael Brown',
    lastUpdated: '2023-04-25',
    pools: ['Q3 RTL Pool'],
    notes: 'Waived for this quarter due to recent restructuring. Will resume next quarter.'
  }];
  // Filter compliance requirements based on active filters
  const filteredRequirements = complianceRequirements.filter(requirement => {
    // Filter by category
    if (activeCategory !== 'all' && requirement.category !== activeCategory) {
      return false;
    }
    // Filter by status
    if (activeStatus !== 'all' && requirement.status !== activeStatus) {
      return false;
    }
    // Filter by search query
    if (searchQuery && !requirement.title.toLowerCase().includes(searchQuery.toLowerCase()) && !requirement.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  // Get counts for status tabs
  const getStatusCount = (status: ComplianceStatus | 'all') => {
    if (status === 'all') {
      return complianceRequirements.length;
    }
    return complianceRequirements.filter(r => r.status === status).length;
  };
  // Get status badge color
  const getStatusBadgeClass = (status: ComplianceStatus) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'non_compliant':
        return 'bg-red-100 text-red-800';
      case 'waived':
        return 'bg-blue-100 text-blue-800';
    }
  };
  // Get category badge color
  const getCategoryBadgeClass = (category: ComplianceCategory) => {
    switch (category) {
      case 'regulatory':
        return 'bg-purple-100 text-purple-800';
      case 'investor':
        return 'bg-blue-100 text-blue-800';
      case 'internal':
        return 'bg-gray-100 text-gray-800';
      case 'rating_agency':
        return 'bg-indigo-100 text-indigo-800';
    }
  };
  // Format status for display
  const formatStatus = (status: ComplianceStatus) => {
    switch (status) {
      case 'compliant':
        return 'Compliant';
      case 'pending':
        return 'Pending';
      case 'non_compliant':
        return 'Non-Compliant';
      case 'waived':
        return 'Waived';
    }
  };
  // Format category for display
  const formatCategory = (category: ComplianceCategory) => {
    switch (category) {
      case 'regulatory':
        return 'Regulatory';
      case 'investor':
        return 'Investor';
      case 'internal':
        return 'Internal';
      case 'rating_agency':
        return 'Rating Agency';
    }
  };
  return <div className="space-y-6" data-id="element-102">
      <div className="flex justify-between items-center" data-id="element-103">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-104">
          Compliance Tracker
        </h2>
        <div className="flex space-x-3" data-id="element-105">
          <button className="px-4 py-2 bg-background border border-border rounded-full text-foreground/70 hover:bg-muted flex items-center text-sm transition-colors" data-id="element-106">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-107" />
            Export Report
          </button>
          <button className="px-4 py-2 bg-success rounded-full text-foreground/70 hover:bg-success/80 flex items-center text-sm transition-colors" data-id="element-108">
            <FileTextIcon className="w-4 h-4 mr-2" data-id="element-109" />
            Add Requirement
          </button>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-md flex items-start" data-id="element-110">
        <InfoIcon className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-111" />
        <div data-id="element-112">
          <h3 className="font-medium text-blue-800" data-id="element-113">
            About Compliance Tracker
          </h3>
          <p className="text-sm text-blue-700 mt-1" data-id="element-114">
            Monitor and manage compliance requirements from regulatory bodies,
            investors, rating agencies, and internal policies. Track deadlines,
            assign responsibilities, and ensure all requirements are met on
            time.
          </p>
        </div>
      </div>
      <div className="border-b border-gray-200" data-id="element-115">
        <nav className="-mb-px flex space-x-8" data-id="element-116">
          <button onClick={() => setActiveStatus('all')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeStatus === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-117">
            All ({getStatusCount('all')})
          </button>
          <button onClick={() => setActiveStatus('compliant')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeStatus === 'compliant' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-118">
            <div className="flex items-center" data-id="element-119">
              <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-120" />
              Compliant ({getStatusCount('compliant')})
            </div>
          </button>
          <button onClick={() => setActiveStatus('pending')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeStatus === 'pending' ? 'border-yellow-500 text-yellow-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-121">
            <div className="flex items-center" data-id="element-122">
              <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-123" />
              Pending ({getStatusCount('pending')})
            </div>
          </button>
          <button onClick={() => setActiveStatus('non_compliant')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeStatus === 'non_compliant' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-124">
            <div className="flex items-center" data-id="element-125">
              <AlertTriangleIcon className="w-4 h-4 mr-1.5" data-id="element-126" />
              Non-Compliant ({getStatusCount('non_compliant')})
            </div>
          </button>
        </nav>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-127">
        <div className="lg:col-span-1" data-id="element-128">
          <Card data-id="element-129">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-130">Filters</h3>
            <div className="space-y-6" data-id="element-131">
              <div data-id="element-132">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-133">
                  Category
                </h4>
                <div className="space-y-2" data-id="element-134">
                  <label className="flex items-center" data-id="element-135">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={activeCategory === 'all'} onChange={() => setActiveCategory('all')} data-id="element-136" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-137">
                      All Categories
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-138">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={activeCategory === 'regulatory'} onChange={() => setActiveCategory('regulatory')} data-id="element-139" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-140">
                      Regulatory
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-141">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={activeCategory === 'investor'} onChange={() => setActiveCategory('investor')} data-id="element-142" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-143">Investor</span>
                  </label>
                  <label className="flex items-center" data-id="element-144">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={activeCategory === 'internal'} onChange={() => setActiveCategory('internal')} data-id="element-145" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-146">Internal</span>
                  </label>
                  <label className="flex items-center" data-id="element-147">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={activeCategory === 'rating_agency'} onChange={() => setActiveCategory('rating_agency')} data-id="element-148" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-149">
                      Rating Agency
                    </span>
                  </label>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-150">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-151">
                  Due Date
                </h4>
                <div className="space-y-2" data-id="element-152">
                  <label className="flex items-center" data-id="element-153">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" checked data-id="element-154" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-155">
                      Due this week
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-156">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" checked data-id="element-157" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-158">
                      Due this month
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-159">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" data-id="element-160" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-161">Overdue</span>
                  </label>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-162">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-163">Pool</h4>
                <div className="space-y-2" data-id="element-164">
                  <label className="flex items-center" data-id="element-165">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" checked data-id="element-166" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-167">
                      Q3 RTL Pool
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-168">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" checked data-id="element-169" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-170">
                      Bridge Loan Package
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-171">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" checked data-id="element-172" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-173">
                      All Pools
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="lg:col-span-3" data-id="element-174">
          <Card data-id="element-175">
            <div className="flex justify-between items-center mb-6" data-id="element-176">
              <h3 className="text-lg font-medium text-gray-800" data-id="element-177">
                Compliance Requirements
              </h3>
              <div className="flex items-center" data-id="element-178">
                <div className="relative" data-id="element-179">
                  <input type="text" placeholder="Search requirements..." className="border border-gray-300 rounded-md pl-9 pr-3 py-2" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-180" />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-181">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-182">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" data-id="element-183" />
                    </svg>
                  </div>
                </div>
                <button className="ml-2 p-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-184">
                  <FilterIcon className="h-4 w-4 text-gray-500" data-id="element-185" />
                </button>
              </div>
            </div>
            <div className="space-y-4" data-id="element-186">
              {filteredRequirements.length === 0 ? <div className="text-center py-8" data-id="element-187">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-188">
                    <CheckCircleIcon className="h-6 w-6 text-gray-400" data-id="element-189" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-190">
                    No requirements found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-191">
                    No compliance requirements match your current filter
                    criteria.
                  </p>
                </div> : filteredRequirements.map(requirement => <div key={requirement.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50" data-id="element-192">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start" data-id="element-193">
                      <div className="flex items-start" data-id="element-194">
                        <div className="mr-4" data-id="element-195">
                          {requirement.status === 'compliant' && <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-196" />}
                          {requirement.status === 'pending' && <ClockIcon className="w-5 h-5 text-yellow-500" data-id="element-197" />}
                          {requirement.status === 'non_compliant' && <AlertTriangleIcon className="w-5 h-5 text-red-500" data-id="element-198" />}
                          {requirement.status === 'waived' && <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-199" />}
                        </div>
                        <div data-id="element-200">
                          <div className="flex items-center" data-id="element-201">
                            <h4 className="font-medium text-gray-900" data-id="element-202">
                              {requirement.title}
                            </h4>
                            <span className="ml-2 text-xs text-gray-500" data-id="element-203">
                              {requirement.id}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-600" data-id="element-204">
                            {requirement.description}
                          </p>
                          <div className="flex flex-wrap items-center mt-2 gap-2" data-id="element-205">
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-206">
                              <CalendarIcon className="w-3 h-3 mr-1" data-id="element-207" />
                              Due: {requirement.dueDate}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center ml-3" data-id="element-208">
                              Assignee: {requirement.assignee}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center ml-3" data-id="element-209">
                              Pools: {requirement.pools.join(', ')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center mt-3 md:mt-0 gap-2" data-id="element-210">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(requirement.status)}`} data-id="element-211">
                          {formatStatus(requirement.status)}
                        </span>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryBadgeClass(requirement.category)}`} data-id="element-212">
                          {formatCategory(requirement.category)}
                        </span>
                      </div>
                    </div>
                    {requirement.notes && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-213">
                        <p className="text-sm text-gray-600" data-id="element-214">
                          <span className="font-medium" data-id="element-215">Notes:</span>{' '}
                          {requirement.notes}
                        </p>
                      </div>}
                    <div className="mt-3 flex justify-end" data-id="element-216">
                      <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center" data-id="element-217">
                        View Details <ArrowRightIcon className="ml-1 w-3 h-3" data-id="element-218" />
                      </button>
                    </div>
                  </div>)}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}